import { OpenImgContextProvider } from 'openimg/react'
import {
	data,
	Link,
	Links,
	Meta,
	Outlet,
	Scripts,
	ScrollRestoration,
	useLoaderData,
	useMatches,
} from 'react-router'
import { HoneypotProvider } from 'remix-utils/honeypot/react'
import { type Route } from './+types/root.ts'
import appleTouchIconAssetUrl from './assets/favicons/apple-touch-icon.png'
import faviconAssetUrl from './assets/favicons/favicon.svg'
import { GeneralErrorBoundary } from './components/error-boundary.tsx'
import { AppProgress } from './components/progress-bar.tsx'
import { useToast } from './components/toaster.tsx'
import { Button } from './components/ui/button.tsx'
import { href as iconsHref } from './components/ui/icon.tsx'
import { AppToaster } from './components/ui/sonner.tsx'
import { UserDropdown } from './components/user-dropdown.tsx'
import {
	ThemeSwitch,
	useOptionalTheme,
	useTheme,
} from './routes/resources/theme-switch.tsx'
import oyStyleSheetUrl from './styles/oy.css?url'
import tailwindStyleSheetUrl from './styles/tailwind.css?url'
import { getUserId, logout } from './utils/auth.server.ts'
import { ClientHintCheck, getHints } from './utils/client-hints.tsx'
import { prisma } from './utils/db.server.ts'
import { getEnv } from './utils/env.server.ts'
import { pipeHeaders } from './utils/headers.server.ts'
import { honeypot } from './utils/honeypot.server.ts'
import { combineHeaders, getDomainUrl, getImgSrc } from './utils/misc.tsx'
import { useNonce } from './utils/nonce-provider.ts'
import { type Theme, getTheme } from './utils/theme.server.ts'
import { makeTimings, time } from './utils/timing.server.ts'
import { getToast } from './utils/toast.server.ts'
import { useOptionalUser } from './utils/user.ts'

export const links: Route.LinksFunction = () => {
	return [
		// Preload svg sprite as a resource to avoid render blocking
		{ rel: 'preload', href: iconsHref, as: 'image' },
		{
			rel: 'icon',
			href: '/favicon.ico',
			sizes: '48x48',
		},
		{ rel: 'icon', type: 'image/svg+xml', href: faviconAssetUrl },
		{ rel: 'apple-touch-icon', href: appleTouchIconAssetUrl },
		{
			rel: 'manifest',
			href: '/site.webmanifest',
			crossOrigin: 'use-credentials',
		} as const, // necessary to make typescript happy
		{ rel: 'stylesheet', href: tailwindStyleSheetUrl },
		// the herontwerp primitives (plan §1.7) — unlayered, after Tailwind
		{ rel: 'stylesheet', href: oyStyleSheetUrl },
	].filter(Boolean)
}

export const meta: Route.MetaFunction = ({ data }) => {
	return [
		{ title: data ? 'Oyka' : 'Fout in Oyka' },
		{
			name: 'description',
			content: 'Rustige gezinscoördinatie: samen zelfstandig.',
		},
	]
}

export async function loader({ request }: Route.LoaderArgs) {
	const timings = makeTimings('root loader')
	const userId = await time(() => getUserId(request), {
		timings,
		type: 'getUserId',
		desc: 'getUserId in root',
	})

	const user = userId
		? await time(
				() =>
					prisma.user.findUnique({
						select: {
							id: true,
							name: true,
							username: true,
							image: { select: { objectKey: true } },
							roles: {
								select: {
									name: true,
									permissions: {
										select: { entity: true, action: true, access: true },
									},
								},
							},
						},
						where: { id: userId },
					}),
				{ timings, type: 'find user', desc: 'find user in root' },
			)
		: null
	if (userId && !user) {
		console.info('something weird happened')
		// something weird happened... The user is authenticated but we can't find
		// them in the database. Maybe they were deleted? Let's log them out.
		await logout({ request, redirectTo: '/' })
	}
	const { toast, headers: toastHeaders } = await getToast(request)
	const honeyProps = await honeypot.getInputProps()

	return data(
		{
			user,
			requestInfo: {
				hints: getHints(request),
				origin: getDomainUrl(request),
				path: new URL(request.url).pathname,
				userPrefs: {
					theme: getTheme(request),
				},
			},
			ENV: getEnv(),
			toast,
			honeyProps,
		},
		{
			headers: combineHeaders(
				{ 'Server-Timing': timings.toString() },
				toastHeaders,
			),
		},
	)
}

export const headers: Route.HeadersFunction = pipeHeaders

function Document({
	children,
	nonce,
	theme = 'light',
	env = {},
}: {
	children: React.ReactNode
	nonce: string
	theme?: Theme
	env?: Record<string, string | undefined>
}) {
	const allowIndexing = ENV.ALLOW_INDEXING !== 'false'
	return (
		<html lang="nl" className={`${theme} h-full overflow-x-hidden`}>
			<head>
				<ClientHintCheck nonce={nonce} />
				<Meta />
				<meta charSet="utf-8" />
				<meta
					name="viewport"
					content="width=device-width,initial-scale=1,viewport-fit=cover"
				/>
				{/* ADR-025 (4.3): the Home-Screen install is named after the product */}
				<meta name="apple-mobile-web-app-title" content="Oyka" />
				{allowIndexing ? null : (
					<meta name="robots" content="noindex, nofollow" />
				)}
				<Links />
			</head>
			<body className="bg-background text-foreground">
				{children}
				<script
					nonce={nonce}
					dangerouslySetInnerHTML={{
						__html: `window.ENV = ${JSON.stringify(env)}`,
					}}
				/>
				<ScrollRestoration nonce={nonce} />
				<Scripts nonce={nonce} />
			</body>
		</html>
	)
}

export function Layout({ children }: { children: React.ReactNode }) {
	// if there was an error running the loader, data could be missing
	const data = useLoaderData<typeof loader | null>()
	const nonce = useNonce()
	const theme = useOptionalTheme()
	return (
		<Document nonce={nonce} theme={theme} env={data?.ENV}>
			{children}
		</Document>
	)
}

function App() {
	const data = useLoaderData<typeof loader>()
	const user = useOptionalUser()
	const theme = useTheme()
	const matches = useMatches()
	// The app shells (/app, /stal, /else, /plan) and the chromeless front
	// doors (/week/*) carry their own chrome and opt out of this one; what is
	// left — marketing, auth, settings, the 404 — gets the small header and
	// footer below (ADR-027: mark + word, one accent, hairlines).
	const hideAppChrome = matches.some(
		(m) => (m.handle as { hideAppChrome?: boolean } | undefined)?.hideAppChrome,
	)
	useToast(data.toast)

	return (
		<OpenImgContextProvider
			optimizerEndpoint="/resources/images"
			getSrc={getImgSrc}
		>
			{hideAppChrome ? (
				<Outlet />
			) : (
				<div className="flex min-h-screen flex-col justify-between">
					<header className="mx-auto flex w-full max-w-[1040px] items-center justify-between px-5 pt-[max(12px,env(safe-area-inset-top))] pb-3 md:px-10 md:pt-6">
						<nav
							aria-label="Site"
							className="flex w-full items-center justify-between gap-4"
						>
							<Logo />
							{user ? (
								<UserDropdown />
							) : data.requestInfo.path === '/login' ? null : (
								<Button asChild variant="default">
									<Link to="/login">Inloggen</Link>
								</Button>
							)}
						</nav>
					</header>

					<div className="flex flex-1 flex-col">
						<Outlet />
					</div>

					<footer className="border-border mx-auto flex w-full max-w-[1040px] items-center justify-between gap-4 border-t px-5 py-5 md:px-10">
						<div className="flex items-center gap-3">
							<Logo />
							<span className="text-muted-foreground text-sm font-semibold">
								samen zelfstandig
							</span>
						</div>
						<ThemeSwitch userPreference={data.requestInfo.userPrefs.theme} />
					</footer>
				</div>
			)}
			<AppToaster
				closeButton
				position="top-center"
				duration={8000}
				mobileOffset={{ top: 'calc(env(safe-area-inset-top, 0px) + 16px)' }}
				theme={theme}
			/>
			<AppProgress />
		</OpenImgContextProvider>
	)
}

export function PawMark({ className = 'size-7' }: { className?: string }) {
	return (
		<svg
			viewBox="0 0 64 64"
			aria-hidden="true"
			className={className}
			fill="currentColor"
		>
			<ellipse
				cx="13"
				cy="27"
				rx="6.5"
				ry="8.5"
				transform="rotate(-20 13 27)"
			/>
			<ellipse cx="26" cy="16" rx="7" ry="9" transform="rotate(-8 26 16)" />
			<ellipse cx="41" cy="16" rx="7" ry="9" transform="rotate(8 41 16)" />
			<ellipse cx="53" cy="27" rx="6.5" ry="8.5" transform="rotate(20 53 27)" />
			<path d="M32.5 29c9.5 0 17.5 7.6 17.5 16.2 0 7.6-6.1 12.3-17.5 12.3S15 52.8 15 45.2C15 36.6 23 29 32.5 29z" />
		</svg>
	)
}

/** The mark in its accent-soft tile plus the word, as in the app shell. */
function Logo() {
	return (
		<Link
			to="/"
			className="group flex items-center gap-2.5 leading-snug"
			aria-label="Oyka"
		>
			<span
				aria-hidden="true"
				className="bg-secondary text-secondary-foreground flex size-9 items-center justify-center rounded-full"
			>
				{/* ADR-025 (7.5): hover → ease, transform only, guarded per element */}
				<PawMark className="size-5 transition-transform ease-[ease] group-hover:-rotate-12 motion-reduce:transition-none" />
			</span>
			<span className="text-foreground text-lg font-extrabold tracking-tight">
				Oyka
			</span>
		</Link>
	)
}

function AppWithProviders() {
	const data = useLoaderData<typeof loader>()
	return (
		<HoneypotProvider {...data.honeyProps}>
			<App />
		</HoneypotProvider>
	)
}

export default AppWithProviders

// this is a last resort error boundary. There's not much useful information we
// can offer at this level.
export const ErrorBoundary = GeneralErrorBoundary
